/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 * Handles login, logout, token refresh, and user state management.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { queryClient } from "@/lib/queryClient";

/**
 * User data from the server (without password)
 */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Authentication context state and methods
 */
interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<string | null>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Token refresh interval - refresh 1 minute before expiry (14 min)
 */
const TOKEN_REFRESH_INTERVAL = 14 * 60 * 1000;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isAuthenticated = !!user && !!accessToken;

  /**
   * Refresh the access token using the refresh token cookie
   */
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        // Refresh failed - user needs to log in again
        setUser(null);
        setAccessToken(null);
        return null;
      }

      const data = await response.json();
      setAccessToken(data.accessToken);
      setGlobalAccessToken(data.accessToken); // Sync immediately for API calls
      setUser(data.user);
      return data.accessToken;
    } catch {
      setUser(null);
      setAccessToken(null);
      setGlobalAccessToken(null);
      return null;
    }
  }, []);

  /**
   * Set up automatic token refresh
   */
  const setupTokenRefresh = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Set up new interval
    refreshIntervalRef.current = setInterval(() => {
      refreshAccessToken();
    }, TOKEN_REFRESH_INTERVAL);
  }, [refreshAccessToken]);

  /**
   * Clear token refresh interval
   */
  const clearTokenRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Błąd logowania");
      }

      const data = await response.json();
      setAccessToken(data.accessToken);
      setGlobalAccessToken(data.accessToken); // Sync immediately for API calls
      setUser(data.user);

      // Start auto-refresh
      setupTokenRefresh();
    },
    [setupTokenRefresh]
  );

  /**
   * Logout - revoke tokens and clear state
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Ignore logout errors - we'll clear state anyway
    }

    // Clear state
    setUser(null);
    setAccessToken(null);
    setGlobalAccessToken(null);
    clearTokenRefresh();

    // Clear all cached query data
    queryClient.clear();
  }, [clearTokenRefresh]);

  /**
   * Update user data (for settings updates)
   */
  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
  }, []);

  /**
   * On mount, try to restore session by refreshing the token
   */
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      const token = await refreshAccessToken();
      if (token) {
        setupTokenRefresh();
      }
      setIsLoading(false);
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      clearTokenRefresh();
    };
  }, [refreshAccessToken, setupTokenRefresh, clearTokenRefresh]);

  const value: AuthContextType = {
    user,
    accessToken,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshAccessToken,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use authentication context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Get the current access token (for use outside of React components)
 * This is used by the query client to add Authorization headers
 */
let globalAccessToken: string | null = null;

export function getAccessToken(): string | null {
  return globalAccessToken;
}

export function setGlobalAccessToken(token: string | null): void {
  globalAccessToken = token;
}

// Create a separate component to sync the token
export function AuthTokenSync() {
  const { accessToken } = useAuth();

  useEffect(() => {
    setGlobalAccessToken(accessToken);
  }, [accessToken]);

  return null;
}
